import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';

import type {
  CatalogPermissionDto,
  EfsUser,
  PasswordResetInput,
  UserCreateInput,
  UserUpdateInput,
} from './types';

/**
 * Wraps `/api/users/*` (system_admin only). Mirrors
 * `backend/src/modules/users/routes.rs`.
 */

export const listUsers = async (
  params: { q?: string; active_only?: boolean } = {},
): Promise<EfsUser[]> => {
  const res = await apiClient.get<ApiSuccess<EfsUser[]>>('/users', { params });
  return res.data.data ?? [];
};

export const createUser = async (body: UserCreateInput): Promise<EfsUser> => {
  // The BE accepts `null` to clear company/facility; omit them when blank
  // so create defaults apply.
  const payload: Record<string, unknown> = { ...body };
  if (!body.company_id) delete payload.company_id;
  if (!body.facility_id) delete payload.facility_id;
  const res = await apiClient.post<ApiSuccess<EfsUser>>('/users', payload);
  return res.data.data;
};

export const updateUser = async (id: string, body: UserUpdateInput): Promise<EfsUser> => {
  // Encode the tri-state: `''` (empty input) maps to JSON null (= clear),
  // anything else is the new value.
  const payload: Record<string, unknown> = {
    display_name: body.display_name,
    active_flag: body.active_flag,
  };
  if ('company_id' in body) payload.company_id = body.company_id ? body.company_id : null;
  if ('facility_id' in body) payload.facility_id = body.facility_id ? body.facility_id : null;
  const res = await apiClient.patch<ApiSuccess<EfsUser>>(`/users/${id}`, payload);
  return res.data.data;
};

export const resetUserPassword = async (id: string, body: PasswordResetInput): Promise<void> => {
  await apiClient.post<ApiSuccess<null>>(`/users/${id}/password`, body);
};

/** Replaces the account's whole permission set. */
export const replaceUserPermissions = async (
  id: string,
  permission_codes: string[],
): Promise<EfsUser> => {
  const res = await apiClient.put<ApiSuccess<EfsUser>>(`/users/${id}/permissions`, {
    permission_codes,
  });
  return res.data.data;
};

/**
 * Party permission catalog. Omitting `companyId` scopes the list to the
 * caller's own company (the only form vendor admins may use).
 */
export const fetchPermissionCatalog = async (
  companyId?: string,
): Promise<CatalogPermissionDto[]> => {
  const res = await apiClient.get<ApiSuccess<CatalogPermissionDto[]>>('/permissions', {
    params: companyId ? { company_id: companyId } : {},
  });
  return res.data.data ?? [];
};

/**
 * Master lookups consumed by the user form. Both endpoints already exist
 * in the BE — see `backend/src/modules/companies` and `…/facilities`.
 */

export interface CompanyOption {
  company_id: string;
  display_name: string;
}

export interface FacilityOption {
  facility_id: string;
  facility_name: string;
  company_id: string;
}

export const listCompanyOptions = async (): Promise<CompanyOption[]> => {
  const res = await apiClient.get<ApiSuccess<CompanyOption[]>>('/companies', {
    params: { active_only: true, per_page: 200 },
  });
  return res.data.data ?? [];
};

export const listFacilityOptions = async (): Promise<FacilityOption[]> => {
  const res = await apiClient.get<ApiSuccess<FacilityOption[]>>('/facilities', {
    params: { active_only: true },
  });
  return res.data.data ?? [];
};
